import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Alert, Snackbar,
  CircularProgress, Divider, Breadcrumbs, Link as MuiLink,
  IconButton, Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  CallMade as CallMadeIcon,
  AutoAwesome as AutoAwesomeIcon,
  Inventory2 as InventoryIcon,
} from '@mui/icons-material';
import {
  createGateOut, submitGateOut, cancelGateOut,
  FLOW_TYPES, ELIGIBLE_STATUSES, isPalletEligibleForFlow,
} from '../utils/gateoutApi';
import WorkflowStepper, { buildStepsFromDocStatus } from '../components/widgets/WorkflowStepper';
import StatusPill from '../components/widgets/StatusPill';

// ============================================================================
//  CreateGateOutPage — Phase 2B Session 1
//
//  Rendered as an overlay inside ColdStoreInPage. Receives data via props
//  rather than router state, so no App.js route wiring is required.
//
//  Props:
//    flowType        - 'REPACK' | 'REPRINT' | 'REPROCESS' | 'QC' | 'SHIPMENT'
//    preloadedLines  - array of line objects with .pallets[]
//    onSuccess(goNo) - called after successful submit (or PENDING for shipment)
//    onCancel()      - called when user clicks Cancel
// ============================================================================

const todayISO = () => new Date().toISOString().slice(0, 10);
const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export default function CreateGateOutPage({
  flowType: initialFlowTypeProp,
  preloadedLines: preloadedLinesProp = [],
  onSuccess,
  onCancel,
}) {
  // Use props (overlay mode); fall back to safe defaults for direct route mode if ever needed
  const preloadedLines = preloadedLinesProp;
  const initialFlowType = initialFlowTypeProp || 'REPACK';

  // ---- core state ----
  const [flowType, setFlowType] = useState(initialFlowType);
  const meta = FLOW_TYPES[flowType];

  const [header, setHeader] = useState({
    gateout_date: todayISO(),
    gateout_time: nowHHMM(),
    destination_dept: 'PPC',
    vehicle_no: '',
    driver_name: '',
    driver_mobile: '',
    priority: 'NORMAL',
    reason: '',
    // Shipment-only:
    fcl_no: '',
    fcl_month: new Date().toISOString().slice(0, 7), // YYYY-MM
    container_no: '',
    seal_no: '',
    buyer: '',
    destination_port: '',
    stuffing_location: 'BAPATLA - GATE 1',
    departure_date: todayISO(),
    departure_time: '18:00',
  });

  const [lines, setLines] = useState([]); // [{ line_id, product, ..., pallets: [...] }]
  const [palletSel, setPalletSel] = useState({}); // { palletId: bool }
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState(null);

  // Sync default dept when flow type changes
  useEffect(() => {
    setHeader(prev => ({ ...prev, destination_dept: meta.default_dept }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowType]);

  // ---- load pallets from preloaded state ----
  useEffect(() => {
    if (preloadedLines.length === 0) {
      setError('No lines selected. Go back to Cold Store IN and tick lines first.');
      setLoading(false);
      return;
    }

    setLines(preloadedLines);

    // Pre-select all eligible pallets across loaded lines
    const sel = {};
    preloadedLines.forEach(L => {
      (L.pallets || []).forEach(p => {
        if (isPalletEligibleForFlow(p.status, initialFlowType)) sel[p.id] = true;
      });
    });
    setPalletSel(sel);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- pallet selection helpers ----
  const togglePallet = (id) => setPalletSel(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleLine = (line) => {
    const eligible = (line.pallets || []).filter(p => isPalletEligibleForFlow(p.status, flowType));
    const allOn = eligible.length > 0 && eligible.every(p => palletSel[p.id]);
    setPalletSel(prev => {
      const next = { ...prev };
      eligible.forEach(p => { next[p.id] = !allOn; });
      return next;
    });
  };

  // Re-evaluate eligibility when flow type changes (esp. shipment lets out-states through)
  useEffect(() => {
    if (lines.length === 0) return;
    setPalletSel(prev => {
      const next = {};
      lines.forEach(L => (L.pallets || []).forEach(p => {
        const eligible = isPalletEligibleForFlow(p.status, flowType);
        // Keep existing user choice if pallet is still eligible; clear if no longer eligible
        if (eligible) next[p.id] = prev[p.id] !== false; // default true
        // If ineligible, leave out of selection
      }));
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowType]);

  // ---- totals ----
  const totals = useMemo(() => {
    let pallets = 0, cartons = 0, kg = 0;
    lines.forEach(L => (L.pallets || []).forEach(p => {
      if (palletSel[p.id]) {
        pallets++;
        cartons += parseInt(p.cartons, 10) || 0;
        kg += parseFloat(p.total_kgs) || 0;
      }
    }));
    return { pallets, cartons, kg, lb: kg * 2.20 };
  }, [lines, palletSel]);

  // ---- field updaters ----
  const setH = (field, value) => setHeader(prev => ({ ...prev, [field]: value }));

  // ---- validation ----
  const validation = useMemo(() => {
    const errs = [];
    if (!header.gateout_date) errs.push('Gate-out date');
    if (!header.gateout_time) errs.push('Gate-out time');
    if (!header.destination_dept && flowType !== 'SHIPMENT') errs.push('Destination dept');
    if (!header.reason.trim()) errs.push('Reason');
    if (totals.pallets === 0) errs.push('At least one pallet must be selected');
    if (flowType === 'SHIPMENT') {
      if (!header.fcl_no.trim())          errs.push('FCL No');
      if (!header.fcl_month.trim())       errs.push('FCL month');
      if (!header.container_no.trim())    errs.push('Container No');
      if (!header.buyer.trim())           errs.push('Buyer');
      if (!header.destination_port.trim())errs.push('Destination port');
    }
    return errs;
  }, [header, totals, flowType]);

  // ---- submit ----
  const handleSubmit = async () => {
    if (validation.length > 0) {
      setError('Please complete: ' + validation.join(', '));
      return;
    }
    setSubmitting(true);
    setError('');

    const palletIds = Object.keys(palletSel).filter(k => palletSel[k]).map(Number);

    try {
      // Step 1: create draft
      const createRes = await createGateOut({
        flow_type: flowType,
        gateout_date: header.gateout_date,
        gateout_time: header.gateout_time,
        destination_dept: header.destination_dept,
        vehicle_no: header.vehicle_no,
        driver_name: header.driver_name,
        driver_mobile: header.driver_mobile,
        priority: header.priority,
        reason: header.reason,
        pallet_ids: palletIds,
        // Shipment fields (ignored for non-shipment)
        fcl_no: header.fcl_no,
        fcl_month: header.fcl_month,
        container_no: header.container_no,
        seal_no: header.seal_no,
        buyer: header.buyer,
        destination_port: header.destination_port,
        stuffing_location: header.stuffing_location,
        departure_date: header.departure_date,
        departure_time: header.departure_time,
      });

      if (!createRes.success) {
        setError(createRes.error || 'Failed to create gate-out document');
        if (createRes.ineligible_pallets) {
          // Clear ineligible from selection so user can retry
          setPalletSel(prev => {
            const next = { ...prev };
            createRes.ineligible_pallets.forEach(id => { next[id] = false; });
            return next;
          });
        }
        setSubmitting(false);
        return;
      }

      const docId = createRes.doc_id;

      // Step 2: submit
      const subRes = await submitGateOut(docId);
      if (!subRes.success) {
        setError(subRes.error || 'Document was created but submit failed');
        setSubmitting(false);
        return;
      }

      // Success
      const goNo = subRes.gateout_no;
      const isShipment = flowType === 'SHIPMENT';
      setSnack({
        severity: 'success',
        msg: isShipment
          ? `📨 Shipment submitted (Doc #${docId}). Awaiting approval.`
          : `✅ Gate-Out ${goNo} executed. ${subRes.pallets_executed} pallets gated out.`,
      });

      // Notify parent after a moment so user can see the snackbar
      setTimeout(() => {
        if (onSuccess) onSuccess(goNo);
      }, 1800);
    } catch (err) {
      console.error('submit error', err);
      setError(err.response?.data?.error || err.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Discard this gate-out and return to inventory?')) {
      if (onCancel) onCancel();
    }
  };

  // ============================================================================
  //  RENDER
  // ============================================================================
  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2, color: '#64748b' }}>Loading selected lines…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ background: '#f1f5f9', minHeight: '100vh' }}>
      {/* PAGE HEADER */}
      <Box sx={{ background: 'white', px: 3, py: 2, borderBottom: '1px solid #e2e8f0' }}>
        <Breadcrumbs sx={{ fontSize: 11.5, mb: 0.75 }}>
          <MuiLink underline="hover" color="inherit" onClick={() => onCancel && onCancel()} sx={{ cursor: 'pointer' }}>
            Cold Store
          </MuiLink>
          <MuiLink underline="hover" color="inherit">Gate-Out</MuiLink>
          <Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>New Document</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.2 }}>
              New Gate-Out Document
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: '#64748b' }}>
              Status: <StatusPill status="DRAFT" size="small" /> · {totals.pallets} pallet{totals.pallets === 1 ? '' : 's'} · {totals.cartons} cartons · {totals.kg.toFixed(2)} kg
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button onClick={handleCancel} disabled={submitting} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting || validation.length > 0}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CallMadeIcon />}
              sx={{
                background: 'linear-gradient(90deg, #C0392B 0%, #8E44AD 50%, #1F618D 100%)',
                fontWeight: 600,
                '&.Mui-disabled': { opacity: 0.5, color: 'white' },
              }}
            >
              {submitting ? 'Submitting…' : `Submit Gate-Out (${totals.pallets})`}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* WORKFLOW STEPPER */}
      <WorkflowStepper steps={buildStepsFromDocStatus('DRAFT', { isShipment: flowType === 'SHIPMENT' })} />

      {/* GLOBAL ERROR */}
      {error && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
        </Box>
      )}

      {/* SECTION 1: PROCESS TYPE SELECTOR */}
      <SectionCard title="1. Select Process Type" subtitle="determines workflow, required fields, and destination tab">
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
          {Object.values(FLOW_TYPES).map(t => (
            <TypeCard
              key={t.id}
              type={t}
              selected={flowType === t.id}
              onClick={() => setFlowType(t.id)}
            />
          ))}
        </Box>
      </SectionCard>

      {/* SECTION 2: DOCUMENT HEADER */}
      <SectionCard title="2. Document Header" subtitle="when, where, why">
        <Box sx={{
          display: 'grid', gap: 1.75,
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        }}>
          {/* GO No — readonly auto-allocated */}
          <FormField label={flowType === 'SHIPMENT' ? 'FCL No *' : 'Gate-Out No'}>
            {flowType === 'SHIPMENT' ? (
              <TextField fullWidth size="small" placeholder="FCL-2026-1024"
                value={header.fcl_no} onChange={(e) => setH('fcl_no', e.target.value)} />
            ) : (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                bgcolor: '#f5edf7', border: '1px dashed #c4b5fd',
                borderRadius: 1, px: 1.25, py: 0.75,
                fontFamily: 'JetBrains Mono, monospace', fontSize: 12, color: '#6d28d9', fontWeight: 600,
              }}>
                <AutoAwesomeIcon sx={{ fontSize: 14 }} /> Auto → {meta.prefix}-2026-XXXX
              </Box>
            )}
          </FormField>

          <FormField label="Date *">
            <TextField fullWidth size="small" type="date"
              value={header.gateout_date} onChange={(e) => setH('gateout_date', e.target.value)} />
          </FormField>
          <FormField label="Time *">
            <TextField fullWidth size="small" type="time"
              value={header.gateout_time} onChange={(e) => setH('gateout_time', e.target.value)} />
          </FormField>
          <FormField label="Destination Dept *">
            <TextField fullWidth size="small" select
              value={header.destination_dept}
              onChange={(e) => setH('destination_dept', e.target.value)}>
              {['PPC','LABEL','GRADING','QC LAB','EXPORT'].map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
            </TextField>
          </FormField>

          <FormField label="Priority">
            <TextField fullWidth size="small" select
              value={header.priority} onChange={(e) => setH('priority', e.target.value)}>
              <MenuItem value="NORMAL">Normal</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="URGENT">Urgent</MenuItem>
            </TextField>
          </FormField>

          <Box sx={{ gridColumn: '1 / -1' }}>
            <FormField label="Reason / Remarks *">
              <TextField fullWidth size="small" multiline rows={2}
                value={header.reason} onChange={(e) => setH('reason', e.target.value)}
                placeholder={
                  flowType === 'REPACK'    ? 'e.g. Re-pack from 10/2 LB to 20/1 LB for new buyer order #4521'  :
                  flowType === 'REPRINT'   ? 'e.g. Brand label change requested by buyer'                       :
                  flowType === 'REPROCESS' ? 'e.g. Re-grading from 21/25 to 26/30 due to size variance'         :
                  flowType === 'QC'        ? 'e.g. Routine batch testing for export certification'              :
                                             'e.g. Outbound shipment per PO-4521'
                } />
            </FormField>
          </Box>
        </Box>
      </SectionCard>

      {/* SECTION 2.5: SHIPMENT-SPECIFIC FIELDS */}
      {flowType === 'SHIPMENT' && (
        <SectionCard title="2a. FCL & Shipment Details" subtitle="shipment-specific fields">
          <Box sx={{
            display: 'grid', gap: 1.75,
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' },
          }}>
            <FormField label="FCL Month *">
              <TextField fullWidth size="small" type="month"
                value={header.fcl_month} onChange={(e) => setH('fcl_month', e.target.value)} />
            </FormField>
            <FormField label="Container No *">
              <TextField fullWidth size="small" placeholder="MSCU 1234567"
                value={header.container_no} onChange={(e) => setH('container_no', e.target.value)} />
            </FormField>
            <FormField label="Seal No">
              <TextField fullWidth size="small" placeholder="SE-998877"
                value={header.seal_no} onChange={(e) => setH('seal_no', e.target.value)} />
            </FormField>
            <FormField label="Buyer / Consignee *">
              <TextField fullWidth size="small"
                value={header.buyer} onChange={(e) => setH('buyer', e.target.value)} />
            </FormField>
            <FormField label="Destination Port *">
              <TextField fullWidth size="small" placeholder="NEW YORK"
                value={header.destination_port} onChange={(e) => setH('destination_port', e.target.value)} />
            </FormField>
            <FormField label="Stuffing Location">
              <TextField fullWidth size="small" select
                value={header.stuffing_location}
                onChange={(e) => setH('stuffing_location', e.target.value)}>
                <MenuItem value="BAPATLA - GATE 1">BAPATLA - GATE 1</MenuItem>
                <MenuItem value="BAPATLA - GATE 2">BAPATLA - GATE 2</MenuItem>
              </TextField>
            </FormField>
            <FormField label="Vehicle / Truck No">
              <TextField fullWidth size="small" placeholder="AP-39-XX-1234"
                value={header.vehicle_no} onChange={(e) => setH('vehicle_no', e.target.value)} />
            </FormField>
            <FormField label="Driver Name">
              <TextField fullWidth size="small"
                value={header.driver_name} onChange={(e) => setH('driver_name', e.target.value)} />
            </FormField>
            <FormField label="Driver Mobile">
              <TextField fullWidth size="small" type="tel" placeholder="9876543210"
                value={header.driver_mobile} onChange={(e) => setH('driver_mobile', e.target.value)} />
            </FormField>
            <FormField label="Departure Date">
              <TextField fullWidth size="small" type="date"
                value={header.departure_date} onChange={(e) => setH('departure_date', e.target.value)} />
            </FormField>
            <FormField label="Departure Time">
              <TextField fullWidth size="small" type="time"
                value={header.departure_time} onChange={(e) => setH('departure_time', e.target.value)} />
            </FormField>
          </Box>
        </SectionCard>
      )}

      {/* SECTION 3: PALLET LINES (with picker) */}
      <SectionCard title={`3. Pallet Lines (${totals.pallets} selected)`} subtitle="uncheck pallets you want to keep in CS">
        <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1, p: 1.25 }}>
          {lines.length === 0 ? (
            <Typography sx={{ p: 2, color: '#64748b', fontSize: 13 }}>
              No pallets to show. Go back to Cold Store IN and pick lines first.
            </Typography>
          ) : lines.map(L => (
            <PickerLine key={L.line_id} line={L} flowType={flowType}
              palletSel={palletSel} togglePallet={togglePallet} toggleLine={toggleLine} />
          ))}
        </Box>

        {/* TOTALS BAR */}
        <Box sx={{
          mt: 1.75, p: 1.5,
          bgcolor: '#f5edf7', border: '1px solid #c4b5fd', borderRadius: 1,
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
        }}>
          <InventoryIcon sx={{ color: '#6d28d9' }} />
          <Box>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Document Totals
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#5b21b6', mt: 0.25, fontFamily: 'JetBrains Mono, monospace' }}>
              {totals.pallets} pallets · {totals.cartons} cartons · {totals.kg.toFixed(2)} kg / {totals.lb.toFixed(2)} lb
            </Typography>
          </Box>
          <Typography sx={{ ml: 'auto', fontSize: 11.5, color: '#64748b' }}>
            On execute, status flips to <StatusPill status={meta.after_status} size="small" />
          </Typography>
        </Box>
      </SectionCard>

      {/* SUCCESS SNACKBAR */}
      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {snack && (
          <Alert severity={snack.severity} variant="filled" sx={{ fontWeight: 600 }}>
            {snack.msg}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
}

// ============================================================================
//  Sub-components
// ============================================================================

function SectionCard({ title, subtitle, children }) {
  return (
    <Box sx={{
      bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 1.5,
      mx: 3, mb: 2, overflow: 'hidden',
    }}>
      <Box sx={{
        bgcolor: '#fafbfc', borderBottom: '1px solid #e2e8f0',
        px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{title}</Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>{subtitle}</Typography>
        )}
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Box>
  );
}

function FormField({ label, children }) {
  return (
    <Box>
      <Typography sx={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4,
        color: '#475569', textTransform: 'uppercase', mb: 0.5,
      }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function TypeCard({ type, selected, onClick }) {
  const baseStyle = {
    border: '2px solid #e2e8f0', borderRadius: 1.5, p: 1.5,
    cursor: 'pointer', textAlign: 'center', bgcolor: 'white',
    transition: 'all 120ms',
    '&:hover': { transform: 'translateY(-1px)', borderColor: '#94a3b8' },
  };
  const selectedStyle = selected ? {
    borderColor: type.color, bgcolor: type.light,
    boxShadow: '0 4px 12px rgba(15,23,42,0.1)',
    transform: 'translateY(-1px)',
  } : {};
  return (
    <Box onClick={onClick} sx={{ ...baseStyle, ...selectedStyle }}>
      <Typography sx={{ fontSize: 22, mb: 0.5 }}>{type.icon}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{type.label}</Typography>
      <Typography sx={{ fontSize: 10.5, color: '#64748b', mt: 0.25 }}>{type.desc}</Typography>
      <Box sx={{
        display: 'inline-block', mt: 0.75,
        fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: '#64748b',
        bgcolor: '#f8fafc', px: 0.75, borderRadius: 0.5,
      }}>
        {type.prefix}-2026-XXXX
      </Box>
    </Box>
  );
}

function PickerLine({ line, flowType, palletSel, togglePallet, toggleLine }) {
  const eligible = (line.pallets || []).filter(p => isPalletEligibleForFlow(p.status, flowType));
  const allOn = eligible.length > 0 && eligible.every(p => palletSel[p.id]);
  const someOn = eligible.some(p => palletSel[p.id]);

  return (
    <Box sx={{
      bgcolor: 'white', border: '1px solid #e2e8f0', borderLeft: '3px solid #8E44AD',
      borderRadius: 1, p: 1.5, mb: 1, '&:last-child': { mb: 0 },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <input
          type="checkbox"
          checked={allOn}
          ref={el => { if (el) el.indeterminate = someOn && !allOn; }}
          onChange={() => toggleLine(line)}
          style={{ width: 16, height: 16, cursor: 'pointer' }}
        />
        <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
          {line.product} · {line.grade} · {line.pack_size} · {line.brand} · Lot {line.lot_no}
        </Typography>
        <Typography sx={{ ml: 'auto', fontSize: 10.5, color: '#64748b' }}>
          {eligible.length} eligible / {line.pallets?.length || 0} total
        </Typography>
      </Box>
      {(line.pallets || []).map(p => {
        const isEligible = isPalletEligibleForFlow(p.status, flowType);
        return (
          <Box key={p.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: '24px 100px 110px 70px 110px 1fr',
              gap: 1.25, alignItems: 'center',
              py: 0.5, pl: 3.75, pr: 1,
              fontSize: 11.5,
              opacity: isEligible ? 1 : 0.5,
              '&:hover': { bgcolor: isEligible ? '#fafbfc' : 'transparent', borderRadius: 0.5 },
            }}>
            <input type="checkbox"
              checked={!!palletSel[p.id]}
              disabled={!isEligible}
              onChange={() => togglePallet(p.id)} />
            <Typography sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: '#1F618D' }}>
              {p.pallet_id}
            </Typography>
            <Typography sx={{ color: '#64748b' }}>
              {p.cs1 || p.cs2 || '—'}
            </Typography>
            <Typography sx={{ color: '#64748b', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace' }}>
              {p.cartons} ctn
            </Typography>
            <Typography sx={{ color: '#64748b', textAlign: 'right', fontFamily: 'JetBrains Mono, monospace' }}>
              {parseFloat(p.total_kgs || 0).toFixed(2)} kg
            </Typography>
            {!isEligible
              ? <Typography sx={{ fontSize: 10.5, color: '#64748b', fontStyle: 'italic' }}>
                  {p.status} — cannot select for {flowType}
                </Typography>
              : <span />
            }
          </Box>
        );
      })}
    </Box>
  );
}
